import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Tabs,
  Textarea,
  Toast,
  type Column,
} from '../../design';
import { useAuth } from '../../auth/AuthProvider';
import { ApiError } from '../../lib/api';
import { clientsApi, clientStatusTone, inviteTone } from '../../lib/clients';
import {
  CLIENT_GOALS,
  CLIENT_STATUS_LABELS,
  INVITE_LABELS,
  type ClientListItem,
  type PractitionerOption,
} from '../../lib/types';

type Filter = 'all' | 'active' | 'invited' | 'on_hold';

const TABS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'on_hold', label: 'On hold' },
];

function matchesTab(client: ClientListItem, tab: Filter): boolean {
  if (tab === 'all') return true;
  if (tab === 'invited') return client.inviteState !== 'accepted';
  return client.status === tab;
}

export function ClientsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const [tab, setTab] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);
  const [form, setForm] = useState({ name: '', email: '', goal: '', program: '', intakeNotes: '', practitionerId: '' });
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function load() {
    try {
      const res = await clientsApi.list();
      setClients(res.clients);
    } catch {
      setError('Could not load clients.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Filtering and search run over the one fetch. Moving them server-side would
  // need a query-string helper *and* request cancellation — without the latter a
  // fast typist gets an older response overwriting a newer one. Revisit at ~200
  // clients, together with pagination.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return clients.filter((c) => {
      if (!matchesTab(c, tab)) return false;
      if (!q) return true;
      return [c.name, c.email, c.goal, c.program].some((v) => v?.toLowerCase().includes(q));
    });
  }, [clients, tab, query]);

  const counts = useMemo(
    () => Object.fromEntries(TABS.map((t) => [t.value, clients.filter((c) => matchesTab(c, t.value)).length])),
    [clients],
  );

  function openCreate() {
    setForm({ name: '', email: '', goal: '', program: '', intakeNotes: '', practitionerId: '' });
    setFieldErrors({});
    setFormError(null);
    setNotice(null);
    setError(null);
    setWarning(null);
    setOpen(true);
    if (isAdmin && practitioners.length === 0) {
      void clientsApi
        .practitioners()
        .then((r) => setPractitioners(r.practitioners))
        .catch(() => undefined);
    }
  }

  async function onCreate() {
    const errors: { name?: string; email?: string } = {};
    if (!form.name.trim()) errors.name = 'Enter their full name.';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errors.email = 'Enter a valid email address.';
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length > 0) return;

    setCreating(true);
    try {
      const res = await clientsApi.create({
        name: form.name.trim(),
        email: form.email.trim(),
        goal: form.goal || null,
        program: form.program.trim() || null,
        intakeNotes: form.intakeNotes.trim() || null,
        practitionerId: isAdmin && form.practitionerId ? form.practitionerId : null,
      });
      setOpen(false);
      await load();
      if (res.inviteSent) {
        setNotice(`${res.client.name} will get an email with a link to set their password.`);
      } else {
        // The server deliberately does not fail creation when SMTP does, so the
        // account exists — say so plainly rather than implying an email went out.
        setWarning(
          `${res.client.name} was added, but the invitation email could not be sent. Open them and use Resend invite.`,
        );
      }
    } catch (err) {
      // 409 belongs on the email field — that is where the reader will look.
      if (err instanceof ApiError && err.status === 409) {
        setFieldErrors({ email: err.message });
      } else {
        setFormError(err instanceof ApiError ? err.message : 'Could not add this client.');
      }
    } finally {
      setCreating(false);
    }
  }

  const columns: Column<ClientListItem>[] = [
    {
      key: 'name',
      header: 'Client',
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <Avatar name={r.name} size="sm" />
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <span style={{ color: 'var(--text-strong)' }}>{r.name}</span>
              {r.inviteState !== 'accepted' && (
                <Badge tone={inviteTone(r.inviteState)}>{INVITE_LABELS[r.inviteState]}</Badge>
              )}
            </span>
            <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{r.email}</span>
          </span>
        </span>
      ),
    },
    { key: 'goal', header: 'Goal', render: (r) => r.goal ?? <span style={{ color: 'var(--text-muted)' }}>—</span> },
    { key: 'program', header: 'Current program', render: (r) => r.program ?? <span style={{ color: 'var(--text-muted)' }}>—</span> },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge dot tone={clientStatusTone(r.status)}>
          {CLIENT_STATUS_LABELS[r.status]}
        </Badge>
      ),
    },
    // Noise when you only have your own roster; only an admin sees everyone's.
    ...(isAdmin
      ? [
          {
            key: 'practitionerName',
            header: 'Practitioner',
            render: (r: ClientListItem) => r.practitionerName ?? <span style={{ color: 'var(--text-muted)' }}>Unassigned</span>,
          } as Column<ClientListItem>,
        ]
      : []),
  ];

  return (
    <>
      {notice && (
        <Toast tone="success" title="Invite sent" onDismiss={() => setNotice(null)}>
          {notice}
        </Toast>
      )}
      {warning && (
        <Toast tone="warning" title="Invite not sent" onDismiss={() => setWarning(null)}>
          {warning}
        </Toast>
      )}
      {error && (
        <Toast tone="danger" title="Something went wrong" onDismiss={() => setError(null)}>
          {error}
        </Toast>
      )}

      <Card pad="sm">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          <Tabs
            variant="pill"
            items={TABS.map((t) => ({ value: t.value, label: `${t.label} (${counts[t.value] ?? 0})` }))}
            value={tab}
            onChange={(v) => setTab(v as Filter)}
          />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--space-4)', alignItems: 'center' }}>
            <Input
              icon="search"
              placeholder="Search name, email, goal"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 240 }}
            />
            <Button icon="user-plus" size="sm" onClick={openCreate}>
              Add client
            </Button>
          </div>
        </div>
      </Card>

      <Card pad="none">
        {loading ? (
          <div style={{ padding: 'var(--space-6)', color: 'var(--text-muted)' }}>Loading…</div>
        ) : clients.length === 0 ? (
          // DataTable renders no message of its own for an empty list.
          <EmptyState
            icon="users"
            title="No clients yet"
            action={
              <Button icon="user-plus" size="sm" onClick={openCreate}>
                Add client
              </Button>
            }
          >
            Add your first client and they&rsquo;ll get an email to set their own password.
          </EmptyState>
        ) : visible.length === 0 ? (
          <EmptyState
            icon="search"
            title="No clients match"
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTab('all');
                  setQuery('');
                }}
              >
                Clear filters
              </Button>
            }
          >
            Try a different search or tab.
          </EmptyState>
        ) : (
          <DataTable rows={visible} columns={columns} onRowClick={(r) => navigate(`/clients/${r.id}`)} />
        )}
      </Card>

      {!loading && clients.length > 0 && (
        <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>
          Showing {visible.length} of {clients.length} {clients.length === 1 ? 'client' : 'clients'}
        </span>
      )}

      <Dialog
        open={open}
        title="Add a client"
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => void onCreate()} disabled={creating}>
              {creating ? 'Sending…' : 'Send invite'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <Field label="Full name" error={fieldErrors.name}>
            <Input
              placeholder="Jane Doe"
              value={form.name}
              invalid={Boolean(fieldErrors.name)}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field
            label="Email"
            hint="We'll email them a link to set their own password."
            error={fieldErrors.email}
          >
            <Input
              type="email"
              placeholder="jane@example.com"
              value={form.email}
              invalid={Boolean(fieldErrors.email)}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Primary goal">
            <Select
              value={form.goal}
              onChange={(e) => setForm({ ...form, goal: e.target.value })}
              options={[{ value: '', label: 'Not set' }, ...CLIENT_GOALS.map((g) => ({ value: g, label: g }))]}
            />
          </Field>
          <Field label="Current program">
            <Input
              placeholder="Deep Sleep Ultrasonic"
              value={form.program}
              onChange={(e) => setForm({ ...form, program: e.target.value })}
            />
          </Field>
          {isAdmin && (
            <Field label="Assigned practitioner" hint="Defaults to you when you have a practitioner profile.">
              <Select
                value={form.practitionerId}
                onChange={(e) => setForm({ ...form, practitionerId: e.target.value })}
                options={[
                  { value: '', label: 'Default' },
                  ...practitioners.map((p) => ({ value: p.id, label: p.name })),
                ]}
              />
            </Field>
          )}
          <Field label="Intake notes">
            <Textarea
              rows={3}
              placeholder="What does the client want to change?"
              value={form.intakeNotes}
              onChange={(e) => setForm({ ...form, intakeNotes: e.target.value })}
            />
          </Field>
          {formError && <span style={{ color: 'var(--status-danger)', font: 'var(--type-body-sm)' }}>{formError}</span>}
        </div>
      </Dialog>
    </>
  );
}
